import React, { useState } from 'react';
import type { FavouriteGroupSummary } from '../utils/favourites';
import MyAccountCard from './MyAccountCard';

interface FavouriteGroupsProps {
    groups: FavouriteGroupSummary[];
    onSelectGroup: (group: FavouriteGroupSummary) => void;
    onCreateGroup: (groupName: string) => Promise<void>;
    onRenameGroup: (group: FavouriteGroupSummary, groupName: string) => Promise<void>;
    onDeleteGroup: (group: FavouriteGroupSummary) => Promise<void>;
    isSubmitting?: boolean;
}

interface GroupEditorState {
    mode: 'create' | 'rename';
    group: FavouriteGroupSummary | null;
}

const FavouriteGroups: React.FC<FavouriteGroupsProps> = ({
    groups,
    onSelectGroup,
    onCreateGroup,
    onRenameGroup,
    onDeleteGroup,
    isSubmitting = false,
}) => {
    const [editorState, setEditorState] = useState<GroupEditorState | null>(null);
    const [groupName, setGroupName] = useState('');
    const [editorError, setEditorError] = useState('');

    const openCreateModal = () => {
        setEditorState({ mode: 'create', group: null });
        setGroupName('');
        setEditorError('');
    };

    const openRenameModal = (group: FavouriteGroupSummary) => {
        setEditorState({ mode: 'rename', group });
        setGroupName(group.name);
        setEditorError('');
    };

    const closeModal = () => {
        if (isSubmitting) {
            return;
        }

        setEditorState(null);
        setGroupName('');
        setEditorError('');
    };

    const handleSave = async () => {
        const normalizedGroupName = groupName.trim();
        if (normalizedGroupName === '' || !editorState) {
            setEditorError('Please enter a group name.');
            return;
        }

        try {
            if (editorState.mode === 'create') {
                await onCreateGroup(normalizedGroupName);
            } else if (editorState.group) {
                await onRenameGroup(editorState.group, normalizedGroupName);
            }
            closeModal();
        } catch (error) {
            setEditorError(error instanceof Error ? error.message : 'Unable to save favourite group.');
        }
    };

    const closeButtonClassName = `selector-modal__close${isSubmitting ? ' is-disabled' : ''}`;
    const cancelButtonClassName = [
        'button',
        'weka-secondary-btn',
        'selector-modal__button',
        'selector-modal__button--secondary',
        isSubmitting ? 'is-disabled' : '',
    ].filter(Boolean).join(' ');
    const saveButtonClassName = [
        'button',
        'weka-primary-btn',
        'selector-modal__button',
        'selector-modal__button--primary',
        isSubmitting || groupName.trim() === '' ? 'is-disabled' : '',
    ].filter(Boolean).join(' ');

    return (
        <>
            <div className="d-flex justify-content-between align-items-center gap-3 mb-4 flex-wrap">
                <div>
                    <h3 className="mb-1">Favourite Groups</h3>
                    <p className="text-muted mb-0">Choose a group to view its saved products.</p>
                </div>
                <button
                    type="button"
                    className="button weka-primary-btn favourite-groups__button"
                    onClick={openCreateModal}
                    disabled={isSubmitting}
                >
                    Create Group
                </button>
            </div>

            {groups.length === 0 ? (
                <div className="woocommerce-message woocommerce-message--info woocommerce-Message woocommerce-Message--info woocommerce-info mb-0">
                    No favourite groups yet.
                </div>
            ) : (
                <div className="row g-3">
                    {groups.map((group) => (
                        <div key={group.slug} className="col-12 col-md-6 col-xl-4">
                            <MyAccountCard
                                variant="page"
                                slug={`favourite-group-${group.slug}`}
                                className="h-100 favourite-groups__card"
                                innerClassName="d-flex flex-column gap-3"
                                wrapContent={false}
                            >
                                    <div className="d-flex justify-content-between align-items-start gap-3">
                                        <div>
                                            <h4 className="h5 mb-1">{group.name}</h4>
                                            <p className="text-muted mb-0">
                                                {group.count} {group.count === 1 ? 'item' : 'items'}
                                                {group.is_default ? ' | Default group' : ''}
                                            </p>
                                        </div>
                                        {!group.is_default && (
                                            <div className="d-flex gap-2">
                                                <button
                                                    type="button"
                                                    className="button weka-secondary-btn button-small favourite-groups__button"
                                                    onClick={() => openRenameModal(group)}
                                                    disabled={isSubmitting}
                                                >
                                                    Rename
                                                </button>
                                                <button
                                                    type="button"
                                                    className="button button-danger button-small favourite-groups__button"
                                                    onClick={() => { void onDeleteGroup(group); }}
                                                    disabled={isSubmitting}
                                                >
                                                    Delete
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    <div className="mt-auto">
                                        <button
                                            type="button"
                                            className="button weka-primary-btn favourite-groups__button"
                                            onClick={() => onSelectGroup(group)}
                                        >
                                            View Group
                                        </button>
                                    </div>
                            </MyAccountCard>
                        </div>
                    ))}
                </div>
            )}

            {editorState && (
                <div className="selector-modal-overlay" onClick={closeModal}>
                    <div className="selector-modal" onClick={(event) => event.stopPropagation()}>
                        <div className="selector-modal__header">
                            <div>
                                <div className="selector-modal__title">
                                    {editorState.mode === 'create' ? 'Create Group' : 'Rename Group'}
                                </div>
                                <p className="selector-modal__subtitle">
                                    {editorState.mode === 'create'
                                        ? 'Create a new favourite group for saved products.'
                                        : 'Update the group name. Products stay in the group.'}
                                </p>
                            </div>
                            <button
                                type="button"
                                aria-label="Close"
                                disabled={isSubmitting}
                                onClick={closeModal}
                                className={closeButtonClassName}
                            >
                                &times;
                            </button>
                        </div>
                        <div className="selector-modal__body">
                            {editorError !== '' && (
                                <div className="woocommerce-error mb-3" role="alert">{editorError}</div>
                            )}
                            <div className="selector-modal__input-row">
                                <input
                                    type="text"
                                    value={groupName}
                                    onChange={(event) => setGroupName(event.currentTarget.value)}
                                    placeholder="Group name"
                                    disabled={isSubmitting}
                                    className="selector-modal__input"
                                />
                            </div>
                            <div className="selector-modal__actions selector-modal__actions--end">
                                <button
                                    type="button"
                                    onClick={closeModal}
                                    disabled={isSubmitting}
                                    className={cancelButtonClassName}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={() => { void handleSave(); }}
                                    disabled={isSubmitting || groupName.trim() === ''}
                                    className={saveButtonClassName}
                                >
                                    {isSubmitting ? 'Saving...' : 'Save Group'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default FavouriteGroups;
